import { Layout } from "@jambushrushti/components/layout/Layout";
import { useEventsFromDB } from "@jambushrushti/hooks/useContent";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { eventDateLabel } from "@jambushrushti/lib/eventDate";

const CommunityEvents = () => {
  const { data: events = [], isLoading, error, refetch } = useEventsFromDB();

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-5 sm:py-8 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <div className="animate-fade-up mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
              <Calendar className="h-3.5 w-3.5 text-primary sm:h-4 sm:w-4" />
              <span className="text-primary">{events.length} Events</span>
            </div>
            <h1 className="font-heading animate-fade-up mb-3 text-3xl font-bold text-foreground delay-100 sm:mb-6 sm:text-5xl">
              Events & Workshops
            </h1>
            <p className="font-body animate-fade-up text-base text-muted-foreground delay-200 sm:text-lg">
              Join our lectures, workshops, festivals, and community gatherings
              dedicated to learning and spiritual growth.
            </p>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="bg-background py-6 sm:py-10 lg:py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div role="alert" className="space-y-4 py-16 text-center">
              <p className="text-destructive">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="mx-auto max-w-6xl">
              {events.length === 0 ? (
                <div className="py-16 text-center">
                  <Calendar className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                  <h3 className="font-heading mb-2 text-xl font-semibold text-foreground">
                    No events scheduled
                  </h3>
                  <p className="font-body text-muted-foreground">
                    Check back soon for upcoming events!
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-2 md:hidden">
                    {events.map(event => (
                      <Card key={event.id} className="overflow-hidden">
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt={event.title}
                            className="h-28 w-full object-cover"
                          />
                        )}
                        <CardContent className="p-4">
                          <div className="mb-2 flex items-start justify-between gap-2">
                            <h2 className="font-heading line-clamp-2 text-base font-semibold text-foreground">
                              {event.title}
                            </h2>
                            <Badge className="shrink-0 text-[10px]">
                              Event
                            </Badge>
                          </div>
                          <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                            {event.description ||
                              "Event details will be available soon."}
                          </p>
                          <div className="mb-3 space-y-1.5 text-xs text-muted-foreground">
                            {event.event_date && (
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-primary" />
                                <span>{eventDateLabel(event.event_date)}</span>
                              </div>
                            )}
                            {event.location && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5 text-primary" />
                                <span className="truncate">
                                  {event.location}
                                </span>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-full"
                            asChild
                          >
                            <Link to={`/community/events/${event.id}`}>
                              View Details
                              <ArrowRight className="ml-2 h-3.5 w-3.5" />
                            </Link>
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="hidden overflow-hidden rounded-xl border bg-card shadow-sm md:block">
                    <table className="w-full text-left">
                      <thead className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Event</th>
                          <th className="px-4 py-3 font-semibold">Date</th>
                          <th className="px-4 py-3 font-semibold">Location</th>
                          <th className="px-4 py-3 text-right font-semibold">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {events.map(event => (
                          <tr
                            key={event.id}
                            className="transition-colors hover:bg-muted/30"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {event.image_url ? (
                                  <img
                                    src={event.image_url}
                                    alt=""
                                    className="h-11 w-14 shrink-0 rounded-md object-cover"
                                  />
                                ) : (
                                  <div className="flex h-11 w-14 shrink-0 items-center justify-center rounded-md bg-primary/10">
                                    <Calendar className="h-5 w-5 text-primary" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="font-heading truncate font-semibold text-foreground">
                                    {event.title}
                                  </p>
                                  <p className="line-clamp-1 max-w-md text-xs text-muted-foreground">
                                    {event.description ||
                                      "Event details will be available soon."}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground">
                              {event.event_date
                                ? eventDateLabel(event.event_date)
                                : "—"}
                            </td>
                            <td className="max-w-52 px-4 py-3 text-sm text-muted-foreground">
                              <span className="block truncate">
                                {event.location || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={`/community/events/${event.id}`}>
                                  View
                                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CommunityEvents;
